import React, { useState, useEffect } from "react";
import { faqAPI, handleAPIError } from "../services/api";
import HierarchicalFAQSelector from "./HierarchicalFAQSelector";

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [formData, setFormData] = useState({
    domain: "",           // New: replaces 'category'
    sub_cat_head: "",     // New: first subcategory level
    sub_category: "",     // New: second subcategory level
    question: "",
    answer: "",
    faq_url: "",          // New: replaces 'url'
    is_active: true,
    tags: "",
    // Legacy fields for backward compatibility
    category: "",
    url: "",
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(""); // Legacy
  const [selectedDomain, setSelectedDomain] = useState(""); // New
  const [selectedSubCatHead, setSelectedSubCatHead] = useState(""); // New
  const [selectedSubCategory, setSelectedSubCategory] = useState(""); // New
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [hasUrlFilter, setHasUrlFilter] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFAQs, setTotalFAQs] = useState(0);
  const [limit] = useState(10);

  // Bulk upload states
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateData, setTemplateData] = useState(null);

  // Hierarchy management states
  const [showHierarchyForm, setShowHierarchyForm] = useState(false);
  const [hierarchyFormData, setHierarchyFormData] = useState({
    domain: "",
    sub_cat_head: "",
    sub_category: "",
  });

  // Excel-style editor states
  const [showExcelEditor, setShowExcelEditor] = useState(false);
  const [excelEditorMinimized, setExcelEditorMinimized] = useState(false);
  const [excelData, setExcelData] = useState([
    {
      domain: "",
      sub_cat_head: "",
      sub_category: "",
      question: "",
      answer: "",
      faq_url: "",
      tags: "",
      is_active: true,
    },
  ]);
  const [excelEditing, setExcelEditing] = useState(false);

  // Recycle bin states
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinMinimized, setRecycleBinMinimized] = useState(false);
  const [deletedFAQs, setDeletedFAQs] = useState([]);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);

  // Load FAQs and categories on component mount
  useEffect(() => {
    loadFAQs();
    loadCategories();
  }, [
    currentPage,
    searchQuery,
    selectedCategory,
    selectedDomain,
    selectedSubCatHead,
    selectedSubCategory,
    showActiveOnly,
    hasUrlFilter,
  ]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const params = {
        limit,
        offset: (currentPage - 1) * limit,
      };

      if (searchQuery) params.query = searchQuery;
      // New hierarchical filters
      if (selectedDomain) params.domain = selectedDomain;
      if (selectedSubCatHead) params.sub_cat_head = selectedSubCatHead;
      if (selectedSubCategory) params.sub_category = selectedSubCategory;
      // Legacy category filter (fallback)
      if (selectedCategory && !selectedDomain) params.category = selectedCategory;
      if (showActiveOnly !== null && showActiveOnly !== undefined)
        params.is_active = showActiveOnly;
      if (hasUrlFilter) params.has_url = hasUrlFilter;

      const response = await faqAPI.listFAQs(params);
      setFaqs(response.faqs || []);
      setTotalFAQs(response.total || 0);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await faqAPI.getFAQCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      domain: "",
      sub_cat_head: "",
      sub_category: "",
      question: "",
      answer: "",
      faq_url: "",
      is_active: true,
      tags: "",
      // Legacy fields
      category: "",
      url: "",
    });
    setEditingFAQ(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const submitData = {
        ...formData,
        tags: formData.tags
          ? formData.tags
              .split(",")
              .map((tag) => tag.trim())
              .filter((tag) => tag)
          : [],
      };

      if (editingFAQ) {
        await faqAPI.updateFAQ(editingFAQ.id, submitData);
      } else {
        await faqAPI.createFAQ(submitData);
      }

      resetForm();
      loadFAQs();
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (faq) => {
    setEditingFAQ(faq);
    setFormData({
      domain: faq.domain || faq.category || "",
      sub_cat_head: faq.sub_cat_head || "",
      sub_category: faq.sub_category || "",
      question: faq.question,
      answer: faq.answer,
      faq_url: faq.faq_url || faq.url || "",
      is_active: faq.is_active,
      tags: faq.tags ? faq.tags.join(", ") : "",
      // Legacy fields
      category: faq.category || faq.domain || "",
      url: faq.url || faq.faq_url || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (faqId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this FAQ? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await faqAPI.deleteFAQ(faqId);
      loadFAQs();
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (faqId) => {
    try {
      await faqAPI.toggleFAQStatus(faqId);
      loadFAQs();
    } catch (err) {
      setError(handleAPIError(err));
    }
  };

  // Download template function
  const downloadTemplate = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Domain,SubCatHead,SubCategory,Question,Answer,FAQ_URL,Tags,IsActive\n" +
      "Student Welfare,Student Care Centers,,How do I submit a leave application?,Submit through the student portal under Leave Application section,https://portal.edu/leave,leave application,TRUE\n" +
      "Academic Services,Registration,Course Enrollment,How do I register for classes?,Log into the student portal and navigate to Registration > Course Enrollment,https://portal.edu/registration,registration enrollment courses,TRUE\n" +
      "Student Welfare,Student Feedback,,How do I submit student feedback?,Visit the feedback portal and complete the anonymous form,https://portal.edu/feedback,feedback survey,TRUE";

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "faq_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Legacy category management functions (removed - using Domain hierarchy now)
  // const handleAddCategory = async (e) => { ... };
  // const handleEditCategory = async (e) => { ... };
  // const handleDeleteCategory = async (categoryName) => { ... };

  // Excel-style editor functions
  const addExcelRow = () => {
    setExcelData([
      ...excelData,
      {
        domain: "",
        sub_cat_head: "",
        sub_category: "",
        question: "",
        answer: "",
        faq_url: "",
        tags: "",
        is_active: true,
      },
    ]);
  };

  const removeExcelRow = (index) => {
    if (excelData.length > 1) {
      setExcelData(excelData.filter((_, i) => i !== index));
    }
  };

  const updateExcelCell = (rowIndex, field, value) => {
    const newData = [...excelData];
    newData[rowIndex][field] = value;
    setExcelData(newData);
  };

  const handleExcelSubmit = async () => {
    try {
      setExcelEditing(true);
      setError("");

      // Validate mandatory fields
      const validationErrors = [];
      excelData.forEach((row, index) => {
        if (!row.question.trim()) {
          validationErrors.push(`Row ${index + 1}: Question is required`);
        }
        if (!row.answer.trim()) {
          validationErrors.push(`Row ${index + 1}: Answer is required`);
        }
        if (!row.domain.trim()) {
          validationErrors.push(`Row ${index + 1}: Domain is required`);
        }
        if (!row.tags.trim()) {
          validationErrors.push(`Row ${index + 1}: Tags are required`);
        }
      });

      if (validationErrors.length > 0) {
        setError(`Validation Errors:\n${validationErrors.join("\n")}`);
        return;
      }

      // Filter out empty rows (rows with only empty fields)
      const validData = excelData.filter(
        (row) =>
          row.question.trim() &&
          row.answer.trim() &&
          row.domain.trim() &&
          row.tags.trim(),
      );

      if (validData.length === 0) {
        setError(
          "Please enter at least one FAQ with question, answer, domain, and tags",
        );
        return;
      }

      // Convert to CSV format and upload
      const csvContent = convertFAQToCSV(validData);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const formData = new FormData();
      formData.append("file", blob, "faq_excel_data.csv");

      const result = await faqAPI.bulkUploadFAQs(formData);
      setBulkResult(result);

      if (result.success_count > 0) {
        alert(`✅ Successfully uploaded ${result.success_count} FAQ(s)!`);
        loadFAQs();
        loadCategories();
      }
      if (result.error_count > 0) {
        alert(
          `❌ Error uploading ${result.error_count} FAQ(s). Please check the details.`,
        );
      }

      // Reset form
      setExcelData([
        {
          question: "",
          answer: "",
          category: "",
          url: "",
          tags: "",
          is_active: true,
        },
      ]);
      setShowExcelEditor(false);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setExcelEditing(false);
    }
  };

  const convertFAQToCSV = (data) => {
    const headers = [
      "Question",
      "Answer",
      "Category",
      "URL",
      "Tags",
      "Is Active",
    ];
    const csvRows = [headers.join(",")];

    data.forEach((row) => {
      const values = [
        `"${row.question || ""}"`,
        `"${row.answer || ""}"`,
        `"${row.category || ""}"`,
        `"${row.url || ""}"`,
        `"${row.tags || ""}"`,
        `"${row.is_active}"`,
      ];
      csvRows.push(values.join(","));
    });

    return csvRows.join("\n");
  };

  const handlePasteData = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const rows = pasteData.split("\n").filter((row) => row.trim());

    if (rows.length > 0) {
      const newData = rows.map((row) => {
        const columns = row
          .split("\t")
          .map((col) => col.trim().replace(/^"|"$/g, ""));
        return {
          question: columns[0] || "",
          answer: columns[1] || "",
          category: columns[2] || "",
          url: columns[3] || "",
          tags: columns[4] || "",
          is_active: columns[5] ? columns[5].toLowerCase() === "true" : true,
        };
      });
      setExcelData(newData);
    }
  };

  // Recycle bin functions
  const loadDeletedFAQs = async () => {
    try {
      setRecycleBinLoading(true);
      setError("");

      const deletedFAQs = await faqAPI.getDeletedFAQs();
      setDeletedFAQs(deletedFAQs);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setRecycleBinLoading(false);
    }
  };

  const restoreFAQ = async (faqId) => {
    try {
      setLoading(true);
      setError("");

      await faqAPI.restoreFAQ(faqId);

      // Refresh the recycle bin and main data
      await loadDeletedFAQs();
      await loadFAQs();

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const permanentDeleteFAQ = async (faqId) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this FAQ? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      await faqAPI.permanentDeleteFAQ(faqId);

      // Refresh the recycle bin
      await loadDeletedFAQs();

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleHome = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setShowActiveOnly(true);
    setHasUrlFilter("");
    setCurrentPage(1);
    setShowForm(false);
    setShowBulkUpload(false);
    setBulkResult(null);
    setShowTemplate(false);
    loadFAQs();
  };

  // Bulk upload functions
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const data = await faqAPI.downloadTemplate();
      setTemplateData(data);
      setShowTemplate(true);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      setError("Please select a file to upload");
      return;
    }

    try {
      setBulkUploading(true);
      setError("");

      const formData = new FormData();
      formData.append("file", bulkFile);

      const result = await faqAPI.bulkUploadFAQs(formData);
      setBulkResult(result);

      // Show alert message
      if (result.success_count > 0) {
        alert(`✅ Successfully uploaded ${result.success_count} FAQ(s)!`);
      }
      if (result.error_count > 0) {
        alert(
          `❌ Error uploading ${result.error_count} FAQ(s). Please check the error details below.`,
        );
      }

      // Reload data after successful upload
      await loadFAQs();
      await loadCategories();

      // Reset form
      setBulkFile(null);
      setShowBulkUpload(false);
    } catch (err) {
      const errorMsg = handleAPIError(err);
      setError(errorMsg);
      alert(`❌ Error while uploading FAQ questions: ${errorMsg}`);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setBulkFile(file);
      setError("");
    } else {
      setError("Please select a valid CSV file");
      setBulkFile(null);
    }
  };

  const downloadCSVTemplate = () => {
    if (!templateData) return;

    const headers = templateData.headers.join(",");
    const sampleRows = templateData.sample_rows
      .map((row) =>
        templateData.headers
          .map((header) => `"${row[header] || ""}"`)
          .join(","),
      )
      .join("\n");

    const csvContent = `${headers}\n${sampleRows}`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateData.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalFAQs / limit);

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          FAQ Management
        </h2>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleHome}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Reset all filters and forms to default state"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Home
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Create a new FAQ entry with question, answer, category, and tags"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add New FAQ
          </button>
          <button
            onClick={() => setShowBulkUpload(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Upload multiple FAQs at once using a CSV file"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Bulk FAQ Upload
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Download CSV template with sample data for bulk upload"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download Template
          </button>
          <button
            onClick={() => setShowHierarchyForm(!showHierarchyForm)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Manage FAQ hierarchy - create domains, subcategory heads, and subcategories"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            Manage Hierarchy
          </button>
          <button
            onClick={() => setShowExcelEditor(!showExcelEditor)}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="Excel-style inline editor for bulk FAQ entry"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            Excel Editor
          </button>
          <button
            onClick={() => {
              setShowRecycleBin(!showRecycleBin);
              if (!showRecycleBin) {
                loadDeletedFAQs();
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            title="View and restore deleted FAQs"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Recycle Bin
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-3">
        {/* Search Row */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search FAQs by tags and questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            onClick={loadFAQs}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors"
            title="Search FAQs using the entered query in tags and questions"
          >
            Search
          </button>
        </div>

        {/* Hierarchical Filter Row */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Hierarchical Filters</h3>
          <HierarchicalFAQSelector
            selectedDomain={selectedDomain}
            selectedSubCatHead={selectedSubCatHead}
            selectedSubCategory={selectedSubCategory}
            onDomainChange={(value) => {
              setSelectedDomain(value);
              setSelectedSubCatHead('');
              setSelectedSubCategory('');
            }}
            onSubCatHeadChange={(value) => {
              setSelectedSubCatHead(value);
              setSelectedSubCategory('');
            }}
            onSubCategoryChange={setSelectedSubCategory}
            allowNew={false}
          />
        </div>

        {/* Additional Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={showActiveOnly === null ? "all" : showActiveOnly.toString()}
            onChange={(e) => {
              const value = e.target.value;
              setShowActiveOnly(value === "all" ? null : value === "true");
            }}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            title="Filter FAQs by active/inactive status"
          >
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
            <option value="all">All Status</option>
          </select>
          <select
            value={hasUrlFilter}
            onChange={(e) => setHasUrlFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            title="Filter FAQs by whether they have a related URL link"
          >
            <option value="">All FAQs</option>
            <option value="has_url">Has URL</option>
            <option value="no_url">No URL</option>
          </select>
          <button
            onClick={loadFAQs}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
            title="Reload FAQ list with current filters applied"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Hierarchy Management Form */}
      {showHierarchyForm && (
        <div className="mb-6 p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-red-900">Manage FAQ Hierarchy</h3>
              <p className="text-sm text-red-700 mt-1">Create hierarchical structure for organizing FAQs</p>
            </div>
            <button
              type="button"
              onClick={() => setShowHierarchyForm(false)}
              className="text-red-600 hover:text-red-800"
              title="Close hierarchy form"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          
          <div className="bg-white p-4 rounded-md border border-red-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain (Top Level)
                </label>
                <input
                  type="text"
                  value={hierarchyFormData.domain}
                  onChange={(e) =>
                    setHierarchyFormData({ ...hierarchyFormData, domain: e.target.value })
                  }
                  placeholder="e.g., Student Welfare, Academic Services"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">Top-level category (required)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SubCatHead (First Level)
                </label>
                <input
                  type="text"
                  value={hierarchyFormData.sub_cat_head}
                  onChange={(e) =>
                    setHierarchyFormData({ ...hierarchyFormData, sub_cat_head: e.target.value })
                  }
                  placeholder="e.g., Student Care Centers, Registration"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  disabled={!hierarchyFormData.domain}
                />
                <p className="text-xs text-gray-500 mt-1">First subcategory (optional)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SubCategory (Second Level)
                </label>
                <input
                  type="text"
                  value={hierarchyFormData.sub_category}
                  onChange={(e) =>
                    setHierarchyFormData({ ...hierarchyFormData, sub_category: e.target.value })
                  }
                  placeholder="e.g., Course Enrollment, Leave Application"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  disabled={!hierarchyFormData.domain || !hierarchyFormData.sub_cat_head}
                />
                <p className="text-xs text-gray-500 mt-1">Second subcategory (optional)</p>
              </div>
            </div>

            <div className="bg-red-50 p-3 rounded-md mb-4">
              <p className="text-sm text-red-800">
                <strong>ℹ️ Note:</strong> To create hierarchy elements, simply add them when creating a new FAQ. 
                Domains, SubCatHeads, and SubCategories are automatically created when you save an FAQ with those values.
                This form is for reference and planning your hierarchy structure.
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>How to use hierarchies:</strong><br />
                    1. Click "Add New FAQ" button<br />
                    2. Enter or select Domain (required)<br />
                    3. Enter or select SubCatHead (optional)<br />
                    4. Enter or select SubCategory (optional)<br />
                    5. Fill in question, answer, tags, and save<br />
                    <br />
                    The hierarchy structure will be created automatically!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Form */}
      {showBulkUpload && (
        <div className="mb-6 p-6 bg-green-50 rounded-lg border border-green-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-800">
              Bulk Upload FAQs
            </h3>
            <button
              type="button"
              onClick={() => setShowBulkUpload(false)}
              className="text-green-600 hover:text-green-800"
              disabled={bulkUploading}
              title="Close bulk upload form"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <form onSubmit={handleBulkUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Please use the template format. Download the template above for
                reference.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={bulkUploading || !bulkFile}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
                title="Upload the selected CSV file with FAQ data"
              >
                {bulkUploading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload FAQs
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowBulkUpload(false);
                  setBulkFile(null);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
                disabled={bulkUploading}
                title="Cancel bulk upload and close form"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Upload Result */}
          {bulkResult && (
            <div className="mt-4 p-3 bg-white rounded-md border">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-800">Upload Results:</h4>
                <button
                  type="button"
                  onClick={() => setBulkResult(null)}
                  className="text-gray-500 hover:text-gray-700"
                  title="Close upload results"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="text-sm space-y-1">
                <p className="text-green-600">
                  ✅ Successfully uploaded: {bulkResult.success_count} FAQs
                </p>
                {bulkResult.error_count > 0 && (
                  <p className="text-red-600">
                    ❌ Errors: {bulkResult.error_count} FAQs
                  </p>
                )}
                {bulkResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-red-600">Error Details:</p>
                    <ul className="list-disc list-inside text-red-600 text-xs space-y-1">
                      {bulkResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Excel-style Editor */}
      {showExcelEditor && (
        <div
          className={`mb-6 p-6 bg-yellow-50 rounded-lg border border-yellow-200 ${excelEditorMinimized ? "minimized" : ""}`}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-yellow-800">
              Excel-style FAQ Editor
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setExcelEditorMinimized(!excelEditorMinimized)}
                className="text-yellow-600 hover:text-yellow-800"
                disabled={excelEditing}
                title={excelEditorMinimized ? "Maximize" : "Minimize"}
              >
                {excelEditorMinimized ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowExcelEditor(false)}
                className="text-yellow-600 hover:text-yellow-800"
                disabled={excelEditing}
                title="Close Excel editor"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {excelEditorMinimized ? (
            <div className="text-center py-6">
              <p className="text-yellow-700 text-sm">
                Excel Editor is minimized. Click the maximize button to expand.
              </p>
              <div className="mt-3 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={addExcelRow}
                  className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-2"
                  disabled={excelEditing}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add Row
                </button>
                <button
                  type="button"
                  onClick={handleExcelSubmit}
                  disabled={excelEditing}
                  className="px-4 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-2"
                >
                  {excelEditing ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                        />
                      </svg>
                      Save All
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {/* Controls */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addExcelRow}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-1"
                    disabled={excelEditing}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Add Row
                  </button>
                  <button
                    type="button"
                    onClick={handleExcelSubmit}
                    disabled={excelEditing}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
                  >
                    {excelEditing ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Save All
                      </>
                    )}
                  </button>
                </div>

                {/* Paste Instructions */}
                <div className="bg-red-50 p-3 rounded-md">
                  <p className="text-sm text-red-800">
                    <strong>💡 Tip:</strong> You can paste data directly from
                    Excel! Copy cells from Excel and paste (Ctrl+V) anywhere in
                    the table below. Make sure your Excel columns match:
                    Domain | SubCatHead | SubCategory | Question | Answer | FAQ_URL | Tags | Active
                    (true/false)
                  </p>
                </div>

                {/* Excel-style Table */}
                <div className="overflow-x-auto" onPaste={handlePasteData}>
                  <table className="min-w-full bg-white border border-gray-200 rounded-md">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                          #
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Domain *
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                          SubCatHead
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                          SubCategory
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Question *
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Answer *
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                          FAQ_URL
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Tags *
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                          Active
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {excelData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 py-2 text-xs text-gray-500 border-r border-gray-200">
                            {index + 1}
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.domain}
                              onChange={(e) =>
                                updateExcelCell(index, "domain", e.target.value)
                              }
                              placeholder="Domain *"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.sub_cat_head}
                              onChange={(e) =>
                                updateExcelCell(index, "sub_cat_head", e.target.value)
                              }
                              placeholder="SubCatHead"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.sub_category}
                              onChange={(e) =>
                                updateExcelCell(index, "sub_category", e.target.value)
                              }
                              placeholder="SubCategory"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.question}
                              onChange={(e) =>
                                updateExcelCell(index, "question", e.target.value)
                              }
                              placeholder="Question *"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.answer}
                              onChange={(e) =>
                                updateExcelCell(index, "answer", e.target.value)
                              }
                              placeholder="Answer *"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="url"
                              value={row.faq_url}
                              onChange={(e) =>
                                updateExcelCell(index, "faq_url", e.target.value)
                              }
                              placeholder="https://..."
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.tags}
                              onChange={(e) =>
                                updateExcelCell(index, "tags", e.target.value)
                              }
                              placeholder="tag1, tag2"
                              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 border-r border-gray-200">
                            <input
                              type="checkbox"
                              checked={row.is_active}
                              onChange={(e) =>
                                updateExcelCell(index, "is_active", e.target.checked)
                              }
                              className="w-4 h-4"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-1 py-1 text-center">
                            <button
                              type="button"
                              onClick={() => removeExcelRow(index)}
                              disabled={excelData.length === 1 || excelEditing}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Remove this row"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-gray-600">
                  * Required fields. Add multiple rows and save all at once.
                  Empty rows will be ignored.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Full-Screen Excel Editor Modal */}
      {showExcelEditor && !excelEditorMinimized && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-screen overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b bg-yellow-50">
              <h3 className="text-xl font-semibold text-yellow-800">
                Excel-style FAQ Editor - Full Screen
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExcelEditorMinimized(true)}
                  className="text-yellow-600 hover:text-yellow-800"
                  disabled={excelEditing}
                  title="Minimize to inline view"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setShowExcelEditor(false)}
                  className="text-yellow-600 hover:text-yellow-800"
                  disabled={excelEditing}
                  title="Close"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 h-full overflow-auto">
              <div className="space-y-4">
                {/* Paste Instructions */}
                <div className="bg-red-50 p-4 rounded-md">
                  <p className="text-sm text-red-800">
                    <strong>💡 Tip:</strong> You can paste data directly from
                    Excel! Copy cells from Excel and paste (Ctrl+V) anywhere in
                    the table below. Make sure your Excel columns match:
                    Question | Answer | Category | URL | Tags | Active
                    (true/false)
                  </p>
                </div>

                {/* Controls */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={addExcelRow}
                    className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-2"
                    disabled={excelEditing}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Add Row
                  </button>
                  <button
                    type="button"
                    onClick={handleExcelSubmit}
                    disabled={excelEditing}
                    className="px-4 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-2"
                  >
                    {excelEditing ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Save All
                      </>
                    )}
                  </button>
                </div>

                {/* Excel-style Table */}
                <div
                  className="overflow-x-auto border border-gray-200 rounded-md"
                  onPaste={handlePasteData}
                >
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                          #
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Question *
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Answer *
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Category *
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                          URL
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                          Tags *
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                          Active
                        </th>
                        <th className="px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {excelData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm text-gray-500 border-r border-gray-200">
                            {index + 1}
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.question}
                              onChange={(e) =>
                                updateExcelCell(
                                  index,
                                  "question",
                                  e.target.value,
                                )
                              }
                              placeholder="Question"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.answer}
                              onChange={(e) =>
                                updateExcelCell(index, "answer", e.target.value)
                              }
                              placeholder="Answer"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <select
                              value={row.category}
                              onChange={(e) =>
                                updateExcelCell(
                                  index,
                                  "category",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                              disabled={excelEditing}
                            >
                              <option value="">Select category...</option>
                              {categories.map((category) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <input
                              type="url"
                              value={row.url}
                              onChange={(e) =>
                                updateExcelCell(index, "url", e.target.value)
                              }
                              placeholder="https://..."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <input
                              type="text"
                              value={row.tags}
                              onChange={(e) =>
                                updateExcelCell(index, "tags", e.target.value)
                              }
                              placeholder="tag1, tag2"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-gray-200">
                            <input
                              type="checkbox"
                              checked={row.is_active}
                              onChange={(e) =>
                                updateExcelCell(
                                  index,
                                  "is_active",
                                  e.target.checked,
                                )
                              }
                              className="w-4 h-4"
                              disabled={excelEditing}
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeExcelRow(index)}
                              disabled={excelData.length === 1 || excelEditing}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Remove this row"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-sm text-gray-600">
                  * Required fields. Add multiple rows and save all at once.
                  Empty rows will be ignored.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recycle Bin Modal */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className={`bg-white rounded-lg shadow-xl w-full max-h-screen overflow-hidden ${
              recycleBinMinimized ? "max-w-md" : "max-w-6xl"
            }`}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b bg-red-50">
              <h3 className="text-xl font-semibold text-red-800">
                {recycleBinMinimized
                  ? "🗑️ Recycle Bin"
                  : "Recycle Bin - Deleted FAQs"}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRecycleBinMinimized(!recycleBinMinimized)}
                  className="text-red-600 hover:text-red-800"
                  disabled={recycleBinLoading}
                  title={recycleBinMinimized ? "Maximize" : "Minimize"}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {recycleBinMinimized ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9v-4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15v4.5M15 15h4.5m-4.5 0l5.5 5.5"
                      />
                    )}
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setShowRecycleBin(false)}
                  className="text-red-600 hover:text-red-800"
                  disabled={recycleBinLoading}
                  title="Close"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 h-full overflow-auto">
              {recycleBinMinimized ? (
                <div className="text-center py-4">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <svg
                      className="w-8 h-8 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    <span className="text-red-700 font-medium">
                      {deletedFAQs.length} deleted items
                    </span>
                  </div>
                  <p className="text-red-600 text-sm">
                    Click maximize to view and manage deleted FAQs
                  </p>
                </div>
              ) : recycleBinLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading deleted FAQs...</p>
                </div>
              ) : deletedFAQs.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    className="w-16 h-16 text-gray-400 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Recycle Bin is Empty
                  </h3>
                  <p className="text-gray-600">No deleted FAQs found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-red-50 p-4 rounded-md">
                    <p className="text-sm text-red-800">
                      <strong>💡 Info:</strong> These FAQs were moved to the
                      recycle bin. You can restore them or permanently delete
                      them.
                    </p>
                  </div>

                  <div className="grid gap-4">
                    {deletedFAQs.map((faq) => (
                      <div
                        key={faq.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <svg
                                className="w-5 h-5 text-red-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              <h4 className="font-medium text-gray-900">
                                {faq.question}
                              </h4>
                            </div>

                            <p className="text-sm text-gray-600 mb-2">
                              {faq.answer}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-2">
                              {faq.category && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                  {faq.category}
                                </span>
                              )}
                              {faq.tags && faq.tags.length > 0 && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  {Array.isArray(faq.tags)
                                    ? faq.tags.join(", ")
                                    : faq.tags}
                                </span>
                              )}
                            </div>

                            {faq.url && (
                              <p className="text-sm text-red-600 mb-2">
                                <a
                                  href={faq.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline"
                                >
                                  {faq.url}
                                </a>
                              </p>
                            )}

                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Deleted:</span>{" "}
                              {new Date(faq.deleted_at).toLocaleString()}
                            </div>
                          </div>

                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => restoreFAQ(faq.id)}
                              disabled={loading}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                              title="Restore this FAQ"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                              Restore
                            </button>
                            <button
                              onClick={() => permanentDeleteFAQ(faq.id)}
                              disabled={loading}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1 disabled:opacity-50"
                              title="Permanently delete this FAQ"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Form */}
      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {editingFAQ ? "Edit FAQ" : "Add New FAQ"}
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingFAQ(null);
                setFormData({
                  question: "",
                  answer: "",
                  category: "",
                  url: "",
                  is_active: true,
                  tags: "",
                });
              }}
              className="text-gray-500 hover:text-gray-700"
              title="Close FAQ form"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Hierarchical Selector */}
            <HierarchicalFAQSelector
              selectedDomain={formData.domain}
              selectedSubCatHead={formData.sub_cat_head}
              selectedSubCategory={formData.sub_category}
              onDomainChange={(value) => setFormData({ ...formData, domain: value, category: value })}
              onSubCatHeadChange={(value) => setFormData({ ...formData, sub_cat_head: value })}
              onSubCategoryChange={(value) => setFormData({ ...formData, sub_category: value })}
              allowNew={true}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question *
              </label>
              <textarea
                required
                value={formData.question}
                onChange={(e) =>
                  setFormData({ ...formData, question: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="Enter your FAQ question..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Answer *
              </label>
              <textarea
                required
                value={formData.answer}
                onChange={(e) =>
                  setFormData({ ...formData, answer: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                rows="4"
                placeholder="Enter the answer to this question..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FAQ URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.faq_url}
                  onChange={(e) =>
                    setFormData({ ...formData, faq_url: e.target.value, url: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="https://example.com/help"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Link to additional resources
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags * (comma-separated)
                </label>
                <input
                  type="text"
                  required
                  value={formData.tags}
                  onChange={(e) =>
                    setFormData({ ...formData, tags: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="e.g., registration, enrollment, portal"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Searchable keywords (required)
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="mr-2"
              />
              <label
                htmlFor="is_active"
                className="text-sm font-medium text-gray-700"
              >
                Active (uncheck to make Inactive)
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                title={
                  editingFAQ
                    ? "Update the existing FAQ with new information"
                    : "Create a new FAQ entry"
                }
              >
                {loading
                  ? "Saving..."
                  : editingFAQ
                    ? "Update FAQ"
                    : "Create FAQ"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
                title="Cancel and reset form to default values"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FAQs List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading FAQs...</p>
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No FAQs found.{" "}
            {searchQuery || selectedDomain || selectedSubCatHead || selectedSubCategory
              ? "Try adjusting your filters."
              : "Create your first FAQ!"}
          </div>
        ) : (
          <>
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    {/* Hierarchy Path */}
                    <div className="flex items-center gap-2 mb-2 text-xs flex-wrap">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded font-medium">
                        {faq.domain || faq.category || "No Domain"}
                      </span>
                      {faq.sub_cat_head && (
                        <>
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            {faq.sub_cat_head}
                          </span>
                        </>
                      )}
                      {faq.sub_category && (
                        <>
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                            {faq.sub_category}
                          </span>
                        </>
                      )}
                    </div>

                    <h4 className="font-semibold text-gray-800 mb-2">
                      {faq.question}
                    </h4>
                    <p className="text-gray-600 text-sm mb-2">{faq.answer}</p>
                    <div className="flex flex-wrap gap-2 text-xs mb-2">
                      <span
                        className={`px-2 py-1 rounded ${faq.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                      >
                        {faq.is_active ? "Active" : "Inactive"}
                      </span>
                      {(faq.faq_url || faq.url) && (
                        <span className="bg-cyan-100 text-cyan-800 px-2 py-1 rounded">
                          Has URL
                        </span>
                      )}
                      {faq.tags &&
                        faq.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="bg-orange-100 text-orange-800 px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded transition-colors flex items-center gap-1"
                      title="Edit this FAQ - Modify question, answer, category, tags, or URL"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    <div
                      className="flex items-center"
                      title={`Click to ${faq.is_active ? "deactivate" : "activate"} this FAQ`}
                    >
                      <label className="relative inline-flex items-center cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={faq.is_active}
                          onChange={() => handleToggleStatus(faq.id)}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            faq.is_active
                              ? "bg-green-600 peer-checked:after:translate-x-full peer-checked:after:border-white"
                              : "bg-red-600 after:translate-x-0"
                          }`}
                        ></div>
                        <span className="ml-2 text-xs font-medium text-gray-700">
                          {faq.is_active ? "Active" : "Inactive"}
                        </span>
                        {/* Hover tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                          {faq.is_active
                            ? "Click to deactivate"
                            : "Click to activate"}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                      </label>
                    </div>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded transition-colors flex items-center gap-1"
                      title="Delete this FAQ - This action cannot be undone"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
                {faq.url && (
                  <div className="mt-2">
                    <a
                      href={faq.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:text-red-800 text-sm underline"
                    >
                      View Related Link →
                    </a>
                  </div>
                )}
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-6">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  title="Go to previous page of FAQs"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  title="Go to next page of FAQs"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Template Modal */}
      {showTemplate && templateData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                FAQ CSV Template for Bulk Upload
              </h2>
              <button
                onClick={() => setShowTemplate(false)}
                className="text-gray-500 hover:text-gray-700"
                title="Close template modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-red-50 p-4 rounded-md">
                <h3 className="font-medium text-red-800 mb-2">
                  Instructions:
                </h3>
                <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                  {templateData.instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </div>

              {/* Headers */}
              <div>
                <h3 className="font-medium text-gray-800 mb-2">CSV Headers:</h3>
                <div className="bg-gray-100 p-3 rounded-md">
                  <code className="text-sm">
                    {templateData.headers.join(", ")}
                  </code>
                </div>
              </div>

              {/* Sample Data */}
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Sample Data:</h3>
                <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {templateData.headers.map((header, index) => (
                          <th
                            key={index}
                            className="text-left py-2 px-2 font-medium text-gray-700"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {templateData.sample_rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b">
                          {templateData.headers.map((header, colIndex) => (
                            <td
                              key={colIndex}
                              className="py-2 px-2 text-gray-600"
                            >
                              {row[header] || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Button */}
              <div className="flex justify-center">
                <button
                  onClick={downloadCSVTemplate}
                  className="px-6 py-3 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center gap-2"
                  title="Download the CSV template file to your computer"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download FAQ CSV Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManagement;
